import { useTechDocsReaderPage } from '@backstage/plugin-techdocs-react';
import { PropsWithChildren, useEffect } from 'react';
import Skeleton from '@material-ui/lab/Skeleton';
import { Header, HeaderLabel } from '@backstage/core-components';
import {
  entityUtil,
  replaceWithEntityMetadataValues,
  getDefaultGroupRef,
} from '@appfolio/backstage-plugin-catalog-common';
import { endsWithInvariantCase } from '@appfolio/backstage-plugin-utils-common';
import { useParams } from 'react-router-dom';
import { useDocPathOwnerEntity } from '../hooks/useDocPathOwnerEntity';
import { useExtendedTechDocsMetadata } from '../hooks/useExtendedTechDocsMetadata';
import { capitalize, Typography } from '@material-ui/core';
import { IconLink, SlackIcon } from '@appfolio/backstage-components';
import { EntityRefLink } from '@backstage/plugin-catalog-react';
import { techdocsPlugin } from '@backstage/plugin-techdocs';
import { useRouteRef } from '@backstage/core-plugin-api';

const skeleton = <Skeleton animation="wave" variant="text" height={40} />;

export const TechDocsReaderPageHeader = (props: PropsWithChildren) => {
  const { children } = props;
  const { '*': path = '' } = useParams();
  const {
    title,
    setTitle,
    subtitle,
    setSubtitle,
    entityRef,
    metadata: { value: metadata, loading: metadataLoading },
    entityMetadata: { value: entityMetadata, loading: entityMetadataLoading },
  } = useTechDocsReaderPage();
  useEffect(() => {
    if (!metadata || !entityMetadata) {
      return;
    }
    setTitle(
      replaceWithEntityMetadataValues(
        metadata.site_name,
        entityMetadata.metadata,
      ),
    );
    setSubtitle(() => {
      let { site_description } = metadata;
      if (!site_description || site_description === 'None') {
        site_description = '';
      }
      return replaceWithEntityMetadataValues(
        site_description,
        entityMetadata.metadata,
      );
    });
  }, [metadata, entityMetadata, setTitle, setSubtitle]);
  const { owner } = useDocPathOwnerEntity(entityRef, path);
  const { links } = useExtendedTechDocsMetadata(entityRef, path);
  const getHeaderLink = useRouteRef(techdocsPlugin.routes.root);

  // If there is no entity or techdocs metadata, there's no reason to show the
  // header (hides the header on 404 error pages).
  const noEntityMetadata =
    !entityMetadataLoading && entityMetadata === undefined;
  const noTechDocsMetadata = !metadataLoading && metadata === undefined;
  if (noEntityMetadata || noTechDocsMetadata) {
    return null;
  }
  const slackLinks = (links ?? owner?.metadata.links ?? []).filter(
    ({ title, url }) => {
      try {
        return (
          title && endsWithInvariantCase(new URL(url).hostname, '.slack.com')
        );
      } catch (_) {
        return false;
      }
    },
  );
  const lifecycle = entityMetadata?.spec?.lifecycle;
  return (
    <Header
      type="Documentation"
      typeLink={getHeaderLink()}
      title={title ?? skeleton}
      subtitle={subtitle === '' ? undefined : subtitle ?? skeleton}
    >
      <HeaderLabel
        label={capitalize(entityMetadata?.kind ?? 'entity')}
        value={
          <EntityRefLink
            color="inherit"
            entityRef={entityRef}
            title={entityMetadata?.metadata.title}
            defaultKind="Component"
          />
        }
      />
      {owner ? (
        <HeaderLabel
          label="Page owner"
          value={
            entityUtil(owner).is(getDefaultGroupRef()) ? (
              <Typography variant="body2">Unowned</Typography>
            ) : (
              <EntityRefLink
                color="inherit"
                entityRef={owner}
                defaultKind="Group"
              />
            )
          }
        />
      ) : null}
      {slackLinks?.length ? (
        <HeaderLabel
          label="Improve this page"
          value={slackLinks.map(({ url, title }) => (
            <Typography variant="body2">
              <IconLink
                Icon={SlackIcon}
                to={url}
                text={String(title)}
                color="inherit"
              />
            </Typography>
          ))}
        />
      ) : null}
      {lifecycle ? (
        <HeaderLabel label="Lifecycle" value={String(lifecycle)} />
      ) : null}
      {children}
    </Header>
  );
};
